import './polyfills'
import { createApp } from 'vue'
import AppComponent from './app.vue'
// import { App } from '@capacitor/app'
import router from '@/router'
import store from '@/store'
import { initSentry } from '@/sentry'
import { i18n } from '@/i18n'
import mapboxgl from 'mapbox-gl'
import BannerPlugin from '@/plugins/banner'
import SensorPlugin from '@/plugins/sensor'
import PrimeVue from 'primevue/config'
import Aura from '@primeuix/themes/aura'
import directives from '@/directives' // Import the directives module
import { useLayout } from '@/lib/tailwind/useLayout'
import { PrimeVueToastSymbol } from 'primevue/usetoast'
import ToastEventBus from 'primevue/toasteventbus'
import { createManagedToastService } from '@/lib/toast/managed-toast'

import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import {
    callbackRequiresAuth,
    clearPendingRedirectTarget,
    getCallbackName,
    getPendingRedirectTarget,
    parseIncomingUrl,
    readRedirectTarget,
    rememberPendingRedirectTarget,
} from '@/lib/helpers/deeplink'
// SQLite imports are dynamic to avoid initialization before app mount
// See bootstrapSQLite() for the actual initialization

import './assets/css/app.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import './assets/css/primevue.css'
import './assets/css/animations.css'

if (Capacitor.isPluginAvailable('SplashScreen')) {
    SplashScreen.show()
}

if (typeof document !== 'undefined') {
    const platform = Capacitor.getPlatform()
    document.documentElement.classList.add(`platform-${platform}`)

    if (Capacitor.isNativePlatform()) {
        document.documentElement.classList.add('native-platform')
    }
}

const ensureInterFonts = (() => {
    let loaded = false
    return () => {
        if (loaded) return
        if (typeof navigator !== 'undefined' && !navigator.onLine) return
        loaded = true
        import('./assets/fonts/inter.css').catch((error) => {
            loaded = false
            console.warn('[Fonts] Failed to load Inter fonts', error)
        })
    }
})()

if (typeof window !== 'undefined') {
    ensureInterFonts()
    window.addEventListener('online', ensureInterFonts)
}

const bootstrapSQLite = async () => {
    try {
        const { runCacheCleanup, getCacheSummary } = await import('@/lib/sqlite')

        await runCacheCleanup().catch((error) => console.warn('[Cache] Cleanup failed', error))

        const summary = await getCacheSummary()
        console.info('[Cache] Summary', summary)
    } catch (error) {
        console.warn('[SQLite] Bootstrap failed', error)
    }
}

// Bootstrap application services.
mapboxgl.accessToken = import.meta.env.VITE_APP_MAPBOX_TOKEN

let lastHandledIncomingUrl = null
let lastHandledIncomingUrlAt = 0
const INCOMING_URL_DEDUP_WINDOW_MS = 1000

const navigateFromIncomingUrl = async (url, { replace = false } = {}) => {
    if (!url) return

    const now = Date.now()
    if (url === lastHandledIncomingUrl && now - lastHandledIncomingUrlAt < INCOMING_URL_DEDUP_WINDOW_MS) return

    try {
        const targetLocation = parseIncomingUrl(url)
        const targetRoute = router.resolve(targetLocation)
        rememberPendingRedirectTarget(targetRoute)

        lastHandledIncomingUrl = url
        lastHandledIncomingUrlAt = now

        if (router.currentRoute.value.fullPath === targetRoute.fullPath) {
            return
        }

        const navigate = replace ? router.replace : router.push
        await navigate(targetLocation)
    } catch (error) {
        if (lastHandledIncomingUrl === url) {
            lastHandledIncomingUrl = null
            lastHandledIncomingUrlAt = 0
        }
        clearPendingRedirectTarget()
        console.warn('[Deeplink] Failed to parse incoming URL', url, error)
        await router.push({ name: 'index' })
    }
}

router.afterEach((to) => {
    if (to.name === 'auth.login') {
        if (!readRedirectTarget(to.query.redirect)) {
            clearPendingRedirectTarget()
        }
        return
    }

    const pendingTarget = getPendingRedirectTarget()
    if (!pendingTarget) return

    const pendingRoute = router.resolve(pendingTarget)
    if (!pendingRoute.matched.length) {
        clearPendingRedirectTarget()
        return
    }

    if (pendingRoute.fullPath !== to.fullPath) {
        clearPendingRedirectTarget()
        return
    }

    const activeCallback = getCallbackName(to.query)
    const requiresAuth = activeCallback ? callbackRequiresAuth(activeCallback) : !to.meta?.guest

    if (store.getters['auth/loggedIn'] || !requiresAuth) {
        clearPendingRedirectTarget()
    }
})

// Deep link to open mobile app if installed
// See https://capacitorjs.com/docs/guides/deep-links
CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    navigateFromIncomingUrl(url).catch((navigationError) => {
        console.warn('[Deeplink] Navigation error', navigationError)
    })
})

// Create the vue 3 application.
const app = createApp(AppComponent)
    .use(router)
    .use(store)
    .use(i18n)
    .use(BannerPlugin)
    .use(SensorPlugin)
    .use(directives)
    .use(PrimeVue, {
        theme: {
            preset: Aura,
            options: {
                darkModeSelector: '.dark',
            },
        },
    })

const baseToast = {
    add(message) {
        ToastEventBus.emit('add', message)
    },
    remove(message) {
        ToastEventBus.emit('remove', message)
    },
    removeGroup(group) {
        ToastEventBus.emit('remove-group', group)
    },
    removeAllGroups() {
        ToastEventBus.emit('remove-all-groups')
    },
}

const managedToast = createManagedToastService(baseToast, {
    maxVisible: 3,
    dedupWindowMs: 1500,
})

app.config.globalProperties.$toast = managedToast
app.provide(PrimeVueToastSymbol, managedToast)

app.config.performance = !import.meta.env.PROD

// finally on tablet we want to show the web version of the app
// const isBuildForMobile = false
const isBuildForMobile = import.meta.env.VITE_APP_MOBILE === 'true' || import.meta.env.VITE_APP_MOBILE === true

const isMobile = () => {
    if (isBuildForMobile) {
        return true
    } else if (window.innerWidth >= 1024) {
        return false
        // media queries => md
    } else {
        return true
    }
}
app.config.globalProperties.$isMobile = isMobile()
app.provide('isMobile', app.config.globalProperties.$isMobile)
app.config.globalProperties.$isBuildForMobile = isBuildForMobile
app.provide('isBuildForMobile', isBuildForMobile)

let timer = undefined
const onResize = (e) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
        const vh = window.visualViewport?.height || window.innerHeight
        // console.log(
        //     '### vh',
        //     vh,
        //     document.documentElement.clientHeight,
        //     window.innerHeight,
        //     window.visualViewport?.height
        // )
        document.documentElement.style.setProperty('--vh', `${vh}px`)
        // console.log('###### onResize ###### ', window.innerWidth, window.innerHeight)
        if (app.config.globalProperties.$isMobile !== isMobile()) {
            alert('The display will be adapted to your screen.')
            return location.reload()
        }
    }, 100)
}

// let routerIsReady = false
initSentry(app, router, isBuildForMobile)

// Initiate the application when the router is ready.
router.isReady().then(async () => {
    // routerIsReady = true
    const { primaryColors, surfaces, preset, primary, surface, isDarkMode, updateColors } = useLayout()
    const defaultColorPalette = 'alogo'
    updateColors('primary', defaultColorPalette)
    updateColors('surface', defaultColorPalette)

    if (Capacitor.isNativePlatform()) {
        try {
            const launchData = await CapacitorApp.getLaunchUrl()
            if (launchData?.url) {
                await navigateFromIncomingUrl(launchData.url, { replace: true })
            }
        } catch (error) {
            console.warn('[Deeplink] Failed to read launch URL', error)
        }
    }

    app.mount('#app')

    // Initialize SQLite after app is mounted
    bootstrapSQLite()

    onResize()
    window.addEventListener('resize', onResize)
    window.addEventListener('focusin', onResize)
    window.addEventListener('focusout', onResize)
    window.visualViewport?.addEventListener('resize', onResize)
})
